import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { Event } from "@/types/event";
import { useEventEarlyBird } from "@/hooks/useEventEarlyBird";

/**
 * "Join Nimi Friends at checkout" upsell (Phase 3).
 *
 * v1 scope: Gallery Strolls only. Secret Salon / Dinner Club are out of scope.
 *
 * Every number here is derived live from `get_event_early_bird` + the Friends
 * partner code, so changing the early-bird settings or the Friends percentage
 * takes effect with no deploy. Nothing may be hardcoded.
 */

export const FRIENDS_TIER_SLUG = "friends";
/** Fallback allowlist when app_settings.friends_upsell.allowed_tags is unset. */
export const FRIENDS_UPSELL_DEFAULT_TAGS = ["gallery stroll"];

export type DefaultOnPolicy = "above_breakeven" | "always";

export interface FriendsUpsellConfig {
  /** Canonical package, resolved by primary key from app_settings — never by tier_slug. */
  packageId: string | null;
  packageName: string;
  membershipCost: number;
  hasStripePrice: boolean;
  defaultOnPolicy: DefaultOnPolicy;
  /** Event tags the upsell runs on (app_settings.friends_upsell.allowed_tags). */
  allowedTags: string[];
  hasMembership: boolean;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Upsell config + the caller's membership status via the
 * `get_friends_upsell_state` RPC. Deliberately NOT a direct read/inner join on
 * campaign_packages: its public-read policy is tied to the campaign window,
 * so once the campaign ends the browser could no longer see the package and
 * every member would read as a non-member.
 *
 * Membership rule: status in (active, trialing) with a non-null tier_slug.
 * `paused_until` is deliberately ignored — a paused member keeps their
 * discount today (see useMembershipDiscountStatus), so they must not be shown
 * a "join" upsell either. Don't "fix" this into charging paused members.
 */
export const useFriendsUpsellState = () => {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ["friends-upsell-state", userId ?? "anon"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<FriendsUpsellConfig> => {
      const { data, error } = await (supabase.rpc as any)("get_friends_upsell_state");
      if (error) throw error;
      const d = (data || {}) as Record<string, any>;
      const tags = Array.isArray(d.allowed_tags) ? d.allowed_tags : FRIENDS_UPSELL_DEFAULT_TAGS;
      return {
        packageId: d.package_id || null,
        packageName: d.package_name || "Nimi Friends",
        membershipCost: Number(d.membership_cost || 0),
        hasStripePrice: !!d.has_stripe_price,
        defaultOnPolicy: (d.default_on_policy as DefaultOnPolicy) || "above_breakeven",
        allowedTags: tags.map((t: string) => String(t).toLowerCase()),
        hasMembership: !!d.has_membership,
      };
    },
  });
};

export const useFriendsUpsellConfig = useFriendsUpsellState;

export const useHasActiveMembership = () => {
  const q = useFriendsUpsellState();
  return { ...q, data: q.data?.hasMembership };
};

export interface FriendsDiscount {
  code: string;
  discount_percentage: number;
  applies_to_tags: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
}

/** Active percentage perks attached to the Friends tier. */
export const useFriendsDiscounts = () =>
  useQuery({
    queryKey: ["friends-tier-discounts"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FriendsDiscount[]> => {
      const { data, error } = await supabase
        .from("partner_codes")
        .select("code, discount_percentage, applies_to_tags, valid_from, valid_until")
        .eq("tier_slug", FRIENDS_TIER_SLUG)
        .eq("is_active", true)
        .eq("discount_type", "percentage");

      if (error) throw error;
      return (data || []).map((d) => ({
        code: d.code,
        discount_percentage: Number(d.discount_percentage || 0),
        applies_to_tags: d.applies_to_tags,
        valid_from: d.valid_from,
        valid_until: d.valid_until,
      }));
    },
  });

/**
 * Best Friends percentage for these event tags, restricted to the upsell tag
 * allowlist (app_settings.friends_upsell.allowed_tags; v1: gallery stroll).
 * create-checkout applies the same allowlist server-side.
 */
export const resolveFriendsDiscount = (
  discounts: FriendsDiscount[],
  eventTags: string[],
  allowedTags: string[] = FRIENDS_UPSELL_DEFAULT_TAGS,
): FriendsDiscount | null => {
  const allowed = allowedTags.map((t) => t.toLowerCase());
  const tags = eventTags.map((t) => t.toLowerCase()).filter((t) => allowed.includes(t));
  if (tags.length === 0) return null;

  const now = new Date();
  let best: FriendsDiscount | null = null;

  for (const d of discounts) {
    if (d.valid_from && new Date(d.valid_from) > now) continue;
    if (d.valid_until && new Date(d.valid_until) < now) continue;
    if (!d.discount_percentage || d.discount_percentage >= 100) continue;

    const tagMatch =
      !!d.applies_to_tags && d.applies_to_tags.some((t) => tags.includes(t.toLowerCase()));
    if (!tagMatch) continue;

    if (!best || d.discount_percentage > best.discount_percentage) best = d;
  }

  return best;
};

export interface ProspectiveMemberPrice {
  /** Show the upsell toggle + dual price for this user on this event. */
  show: boolean;
  /** All underlying queries (upsell state, Friends discounts, early bird) have settled. */
  isReady: boolean;
  friendsDiscountPct: number;
  /** What a non-member pays per seat today (early bird applied if active). */
  nonmemberUnitPrice: number;
  /** What a Friends member pays per seat today. */
  memberUnitPrice: number;
  /** Per-seat saving. */
  saving: number;
  membershipCost: number;
  joinTotal: number;
  declineTotal: number;
  earlyBirdActive: boolean;
  defaultOn: boolean;
  packageId: string | null;
  packageName: string;
}

interface ProspectiveMemberPriceArgs {
  event: Pick<Event, "id" | "price" | "tags" | "currency"> | null | undefined;
  seats: number;
  /** Set when another discount path is in play — the upsell must never stack. */
  blocked?: boolean;
}

/**
 * Live member-vs-non-member pricing for a user who is NOT yet a member.
 *
 * ⚠️ `get_event_early_bird` returns `discounted_price` even when `active` is
 * false (configured, window passed). Always gate on `active` — never coalesce.
 */
export const useProspectiveMemberPrice = ({
  event,
  seats,
  blocked = false,
}: ProspectiveMemberPriceArgs): ProspectiveMemberPrice => {
  const configQ = useFriendsUpsellConfig();
  const discountsQ = useFriendsDiscounts();
  const ebQ = useEventEarlyBird(event?.id);
  const config = configQ.data;
  const hasMembership = config?.hasMembership;
  const discounts = discountsQ.data;
  const eb = ebQ.data;

  // True only once EVERY query feeding `show`/prices has settled. Anything
  // that acts on `show` (e.g. resume-after-sign-up) must gate on this, so add
  // any new dependency here too.
  const settled = (q: { isFetched: boolean; isFetching: boolean }) => q.isFetched && !q.isFetching;
  const isReady =
    settled(configQ) && settled(discountsQ) && (!event?.id || settled(ebQ));

  const earlyBirdActive = !!eb?.active;
  const listPrice = Number(event?.price || 0);
  const nonmemberUnitPrice =
    earlyBirdActive && eb?.discounted_price != null ? Number(eb.discounted_price) : listPrice;

  const match = resolveFriendsDiscount(discounts || [], event?.tags || []);
  const friendsDiscountPct = match?.discount_percentage || 0;
  const memberUnitPrice = round2(nonmemberUnitPrice * (1 - friendsDiscountPct / 100));
  const saving = round2(nonmemberUnitPrice - memberUnitPrice);
  const membershipCost = config?.membershipCost || 0;

  const show =
    !blocked &&
    !hasMembership &&
    !!config?.packageId &&
    !!config?.hasStripePrice &&
    friendsDiscountPct > 0 &&
    nonmemberUnitPrice > 0;

  const defaultOn =
    config?.defaultOnPolicy === "always" ? true : saving * seats > membershipCost;

  return {
    show,
    isReady,
    friendsDiscountPct,
    nonmemberUnitPrice,
    memberUnitPrice,
    saving,
    membershipCost,
    joinTotal: round2(memberUnitPrice * seats + membershipCost),
    declineTotal: round2(nonmemberUnitPrice * seats),
    earlyBirdActive,
    defaultOn,
    packageId: config?.packageId ?? null,
    packageName: config?.packageName || "Nimi Friends",
  };
};

/**
 * Card-level helper: the Friends price to show beside today's price for a
 * non-member. Returns null when no upsell applies.
 */
export const useCardFriendsPrice = (
  event: Pick<Event, "id" | "price" | "tags"> | null | undefined,
  todaysPrice: number,
) => {
  const { data: hasMembership } = useHasActiveMembership();
  const { data: discounts } = useFriendsDiscounts();
  const { data: config } = useFriendsUpsellConfig();

  if (hasMembership || !config?.packageId || !config?.hasStripePrice) return null;

  const match = resolveFriendsDiscount(discounts || [], event?.tags || []);
  if (!match || todaysPrice <= 0) return null;

  return {
    memberPrice: round2(todaysPrice * (1 - match.discount_percentage / 100)),
    discountPct: match.discount_percentage,
  };
};
